import React, { useState, useEffect } from 'react'
import { FlatList, Linking } from 'react-native'
import { connect } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import styled, { useTheme } from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import PatientActions from 'APP/Redux/PatientRedux'

import I18n from 'APP/I18n'
import UrlBuilder from 'APP/Helpers/UrlBuilder'
import { logger } from 'APP/Lib/Datadog'

import Config from 'APP/Config'
import { routeNameToAnalyticsName } from 'APP/Nav'

import { Images } from 'APP/Themes'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import Alert from 'APP/Converse/Alert'
import TextInput from 'APP/Converse/Inputs/TextInput'
import Button from 'APP/Converse/Button/index'
import ChallengedInvitationFooter from 'APP/Components/ChallengedInvitationFooter'
import LogoHeader from 'APP/Components/LogoHeader/index'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const ButtonContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const TextInputContainer = styled.View`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 4}px;
`
// Workaround - because separators only add space between items (good), but then the
// container doesnt grow to account for the shadows around each item.
const ItemSeparator = styled.View`
  height: ${({ theme }) => theme.metrics.baseMargin - theme.metrics.baseMargin / 4}px;
`

export const ChallengedEnrolmentUniqueIdScreen = ({
  analyticsName,
  busy,
  challengedInvitation,
  dispatchChallengeInvitation,
  forceShowAllFieldErrors,
}) => {
  const theme = useTheme()
  const navigation = useNavigation()
  const [fieldStates, setFieldStates] = useState({})

  const fields = challengedInvitation.challengeForm?.fields?.map?.((field, index) => {
    const fieldId = `field-${index}`
    const state = fieldStates[fieldId] || { value: '', error: undefined }

    const setState = (newState) => {
      setFieldStates((prev) => ({ ...prev, [fieldId]: newState }))
    }

    const validate = () => {
      const { value } = state

      if (!value?.trim()) {
        setState({ ...state, error: I18n.serverString(field.requiredError) })
        return false
      } else {
        setState({ ...state, error: undefined })
        return true
      }
    }

    return {
      data: field,
      state,
      setState,
      validate,
    }
  })

  useEffect(() => {
    if (!forceShowAllFieldErrors) return

    fields.forEach((field) => {
      field.setState({ ...field.state, error: I18n.serverString(field.data.invalidError) })
    })
  }, [forceShowAllFieldErrors])

  const handleClaimAccess = () => {
    const allFieldsAreValid = fields.every((field) => field.validate())

    if (!allFieldsAreValid) return

    dispatchChallengeInvitation(
      challengedInvitation,
      fields.map((field) => field.state.value)
    )
  }

  const handleSupport = async () => {
    if (Config.CUSTOM_SUPPORT_PAGE) {
      navigation.navigate('contactUs')
      return
    }

    const url = UrlBuilder.supportEmailUrl()
    const canOpen = await Linking.canOpenURL(url)

    if (!canOpen) return

    try {
      await Linking.openURL(url)
    } catch (e) {
      logger.error('[ChallengedEnrolmentUniqueIdScreen] Error opening contact us link', { url }, e)

      Alert.alert(I18n.t('CannotOpenEmail.title'), I18n.t('CannotOpenEmail.body'), [
        { text: I18n.t('CannotOpenEmail.ok') },
      ])
    }
  }

  const getLocalizedTooltipConfig = (toolTipConfig) => {
    if (!toolTipConfig) return null

    const config = {}
    const title = I18n.serverString(toolTipConfig.title)
    const subtitle = I18n.serverString(toolTipConfig.subtitle)
    const img = I18n.serverString(toolTipConfig.img)

    if (title) config.text = title
    if (subtitle) config.subtitle = subtitle
    if (img) config.img = img

    return Object.keys(config).length > 0
      ? { ...config, iconColor: theme.colors.buttonPrimary }
      : null
  }

  const renderInputField = ({ item: { data, state, setState, validate } }) => {
    const tooltipConfig = getLocalizedTooltipConfig(data.infoTip)

    return (
      <TextInputContainer>
        <TextInput
          value={state.value}
          error={state.error}
          placeholder={I18n.serverString(data.label)}
          infotip={I18n.serverString(data.hint)}
          tooltipConfig={tooltipConfig}
          onChangeText={(val) => setState({ ...state, value: val })}
          onFocus={() => setState({ ...state, error: undefined })}
          onBlur={() => validate()}
          noBottomMargin={true}
        />
      </TextInputContainer>
    )
  }

  return (
    <ScreenContainer>
      <ResponsiveScrollView>
        <ContentContainer>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.serverString(challengedInvitation.challengeForm.title)}
            subtitle={I18n.serverString(challengedInvitation.challengeForm.body)}
          />

          <FlatList
            data={fields}
            keyExtractor={(item) => `${challengedInvitation.id}-${item.data.id}`}
            renderItem={renderInputField}
            ItemSeparatorComponent={() => <ItemSeparator />}
          />

          <ButtonContainer>
            <Button
              variant="primary"
              widthVariant="full"
              title={I18n.t('ChallengedEnrolmentUniqueIdScreen.claimAccessButton')}
              analyticsName={`${analyticsName}.claimAccessButton`}
              isLoading={busy}
              disabled={busy}
              onPress={handleClaimAccess}
            />
            <Button
              variant="tertiary"
              widthVariant="full"
              title={I18n.t('Common.contactSupport')}
              analyticsName={'Common.contactSupport'}
              onPress={handleSupport}
            />
          </ButtonContainer>
          <ChallengedInvitationFooter analyticsName={analyticsName} />
        </ContentContainer>
      </ResponsiveScrollView>
    </ScreenContainer>
  )
}

const mapStateToProps = (state, props) => {
  const { patient } = state

  return {
    ...props.route?.params,
    analyticsName: routeNameToAnalyticsName(props.route?.name),
    busy: patient?.challengeInvitationRunning ?? false,
    forceShowAllFieldErrors: patient?.challengeInvitationError ?? null,
  }
}

const mapDispatchToProps = (dispatch) => ({
  dispatchChallengeInvitation: (invitationId, uniqueIdentifier) =>
    dispatch(PatientActions.challengeInvitationRequest(invitationId, uniqueIdentifier)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ChallengedEnrolmentUniqueIdScreen)
